import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Calculator, Percent, ArrowRight } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => `${today().slice(0, 7)}-01`;
const curMonth = () => today().slice(0, 7);

export default function Commissions() {
  const { can } = useAuth();
  const canView = can("maas", "view");
  const canApprove = can("maas", "approve");

  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(today());
  const [payrollMonth, setPayrollMonth] = useState(curMonth());
  const [repFilter, setRepFilter] = useState("all");
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState(new Set()); // bordroya aktarılacak personeller

  const eligible = (r) => r.employee_id && r.commission_enabled && r.commission > 0;

  // Personel filtresi employees kayıtlarından gelir (prim ayarları personel kartında)
  useEffect(() => {
    if (!canView) return;
    api.get("/commissions/rates")
      .then((r) => setEmployees(r.data?.items || []))
      .catch(() => {});
  }, [canView]);

  const calculate = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/commissions/calculate", { params: { start, end } });
      setResult(data);
      // Varsayılan: aktarılabilir tüm personeller seçili gelir
      setSelected(new Set((data.rows || []).filter(eligible).map((r) => r.employee_id)));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const toggleOne = (employeeId, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(employeeId); else next.delete(employeeId);
      return next;
    });
  };

  const transfer = async () => {
    if (!result) return;
    const rows = (result.rows || []).filter((r) => eligible(r) && selected.has(r.employee_id));
    if (!rows.length) { toast.error("Aktarım için seçili personel yok"); return; }
    if (!window.confirm(
      `${rows.length} personel için toplam ₺${fmt(rows.reduce((s, r) => s + r.commission, 0))} prim, ` +
      `${payrollMonth} bordrosuna "prim" olarak işlenecek. Onaylıyor musunuz?`
    )) return;
    setTransferring(true);
    try {
      const { data } = await api.post("/commissions/transfer", {
        start, end, payroll_month: payrollMonth,
        employee_ids: rows.map((r) => r.employee_id),
      });
      if (data.transferred > 0) {
        toast.success(`${data.transferred} personel için ₺${fmt(data.total_amount)} prim ${data.payroll_month} bordrosuna aktarıldı`);
      }
      if (data.skipped_existing?.length) {
        toast.warning(`Zaten aktarılmış (atlandı): ${data.skipped_existing.join(", ")}`);
      }
      if (data.transferred === 0 && !data.skipped_existing?.length) {
        toast.info("Aktarılacak kayıt oluşmadı");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTransferring(false);
    }
  };

  const rows = useMemo(() => {
    const all = result?.rows || [];
    if (repFilter === "all") return all;
    return all.filter((r) => r.employee_id === repFilter);
  }, [result, repFilter]);
  const transferable = rows.filter(eligible);
  const selectedRows = transferable.filter((r) => selected.has(r.employee_id));
  const allChecked = transferable.length > 0 && selectedRows.length === transferable.length;
  const someChecked = selectedRows.length > 0 && !allChecked;
  const toggleAll = (checked) => {
    setSelected(checked ? new Set(transferable.map((r) => r.employee_id)) : new Set());
  };

  if (!canView) {
    return (
      <div className="p-6">
        <Card className="p-8 text-center text-slate-400 bg-slate-900 border-slate-800" data-testid="commission-no-access">
          Bu sayfayı görüntüleme yetkiniz yok (Maaş Hesaplama modülü).
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Prim / Komisyon"
        subtitle="Müşteri temsilcisi (personel) bazında dönemsel prim hesaplama"
        breadcrumbs={["İnsan Kaynakları", "Prim / Komisyon"]}
      />
      <div className="px-4 pb-6 space-y-4">
        <Card className="p-4 bg-slate-900 border-slate-800" data-testid="commission-filters">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">Dönem Başlangıç</div>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                data-testid="commission-start-date"
                className="bg-slate-950 border-slate-800 text-slate-100 rounded-sm" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">Dönem Bitiş</div>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                data-testid="commission-end-date"
                className="bg-slate-950 border-slate-800 text-slate-100 rounded-sm" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">Personel (Temsilci)</div>
              <Select value={repFilter} onValueChange={setRepFilter}>
                <SelectTrigger data-testid="commission-rep-filter" className="bg-slate-950 border-slate-800 text-slate-100 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="commission-rep-all">Tümü</SelectItem>
                  {employees.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`commission-rep-${p.id}`}>
                      {p.name}{p.commission_enabled ? "" : " (prim kapalı)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">Bordro (Prim) Ayı</div>
              <Input type="month" value={payrollMonth} onChange={(e) => setPayrollMonth(e.target.value)}
                data-testid="commission-payroll-month"
                className="bg-slate-950 border-slate-800 text-slate-100 rounded-sm" />
            </div>
            <Button onClick={calculate} disabled={loading}
              data-testid="commission-calculate-btn"
              className="bg-blue-600 hover:bg-blue-500 text-white">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Calculator className="w-4 h-4 mr-1" />}
              Hesapla
            </Button>
          </div>
          <div className="text-[11px] text-slate-500 mt-2">
            Prim atfı, satışın <b>Müşteri Temsilcisi</b> snapshot'ına göre yapılır (temsilci sonradan değişse de eski satışlar etkilenmez).
            Matrah türü (Faturalanan Satış / Tahsilat), oran ve aktiflik <b>personel kartındaki</b> Prim / Komisyon Ayarları'ndan gelir.
            Teklif, reddedilen ve iptal satışlar dahil edilmez; tutarlar KDV hariç net TRY'dir.
          </div>
        </Card>

        {result && (
          <Card className="bg-slate-900 border-slate-800 overflow-hidden" data-testid="commission-result">
            <div className="px-4 py-3 border-b border-slate-800 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="bg-slate-950 border-slate-700 text-slate-300 font-mono text-[10px] uppercase" data-testid="commission-period-badge">
                {result.start} → {result.end}
              </Badge>
              <Badge variant="outline" className="bg-emerald-950/40 border-emerald-900/60 text-emerald-300 font-mono text-[10px] uppercase" data-testid="commission-total-amount">
                Toplam Prim ₺{fmt(result.total_commission)}
              </Badge>
              {result.skipped_docs > 0 && (
                <Badge variant="outline" className="bg-amber-950/40 border-amber-900/60 text-amber-300 font-mono text-[10px] uppercase" data-testid="commission-skipped-badge">
                  {result.skipped_docs} belge hariç tutuldu
                </Badge>
              )}
            </div>
            {rows.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm" data-testid="commission-empty">
                Bu dönem için kayıt bulunamadı.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-slate-800">
                      <TableHead className="w-10">
                        <Checkbox
                          data-testid="commission-select-all"
                          checked={allChecked ? true : someChecked ? "indeterminate" : false}
                          onCheckedChange={toggleAll}
                          disabled={transferable.length === 0}
                          aria-label="Tümünü Seç"
                        />
                      </TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Personel</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Matrah Türü</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Belge</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Matrah ₺ (Net)</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Oran %</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Hak Edilen Prim ₺</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.key}
                        data-testid={`commission-row-${r.employee_id || r.key}`}
                        className="border-slate-800 hover:bg-slate-800/40">
                        <TableCell className="w-10">
                          <Checkbox
                            data-testid={`commission-select-${r.employee_id || r.key}`}
                            checked={r.employee_id ? selected.has(r.employee_id) : false}
                            onCheckedChange={(v) => r.employee_id && toggleOne(r.employee_id, !!v)}
                            disabled={!eligible(r)}
                            aria-label="Seç"
                          />
                        </TableCell>
                        <TableCell>
                          {r.linked ? (
                            <>
                              <div className="text-slate-100 text-sm font-semibold">{r.employee_name}</div>
                              {r.department && <div className="text-[10px] text-slate-500 font-mono uppercase">{r.department}</div>}
                            </>
                          ) : r.key === "(atanmamis)" ? (
                            <Badge variant="outline" className="bg-amber-950/40 border-amber-900/60 text-amber-300 text-[10px] font-mono uppercase"
                              data-testid="commission-unassigned-badge">
                              Atanmamış (temsilci yok)
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-rose-950/40 border-rose-900/60 text-rose-300 text-[10px] font-mono uppercase"
                              data-testid={`commission-unlinked-${r.email}`}>
                              Personel eşleşmesi yok{r.email ? ` (${r.email})` : ""}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 flex-wrap">
                            <Badge variant="outline" className="bg-slate-950 border-slate-700 text-slate-300 text-[10px] font-mono uppercase"
                              data-testid={`commission-basis-badge-${r.employee_id || r.key}`}>
                              {r.basis_label}
                            </Badge>
                            {r.linked && !r.commission_enabled && (
                              <Badge variant="outline" className="bg-slate-900 border-slate-700 text-slate-500 text-[10px] font-mono uppercase"
                                data-testid={`commission-inactive-${r.employee_id}`}>
                                Prim Kapalı
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono text-slate-300 tabular-nums">{r.doc_count}</TableCell>
                        <TableCell className="text-right font-mono text-slate-100 tabular-nums" data-testid={`commission-basis-amount-${r.employee_id || r.key}`}>
                          {fmt(r.basis_amount)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-slate-400 text-xs" data-testid={`commission-rate-${r.employee_id || r.key}`}>
                          %{r.rate}
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold text-emerald-400 tabular-nums" data-testid={`commission-amount-${r.employee_id || r.key}`}>
                          {fmt(r.commission)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <div className="px-4 py-3 border-t border-slate-800 flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[11px] text-slate-500 font-mono" data-testid="commission-transferable-count">
                {selectedRows.length}/{transferable.length} personel seçili · Bordro ayı: {payrollMonth}
              </div>
              {canApprove && (
                <Button onClick={transfer} disabled={transferring || loading || selectedRows.length === 0}
                  data-testid="commission-transfer-btn"
                  className="bg-emerald-600 hover:bg-emerald-500 text-white">
                  {transferring ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ArrowRight className="w-4 h-4 mr-1" />}
                  Seçilenleri Bordroya Aktar ({selectedRows.length})
                </Button>
              )}
            </div>
          </Card>
        )}

        {!result && (
          <Card className="p-10 text-center bg-slate-900 border-slate-800" data-testid="commission-hint">
            <Percent className="w-8 h-8 text-slate-600 mx-auto mb-2" />
            <div className="text-slate-400 text-sm">Dönem seçip <b>Hesapla</b>'ya basın.</div>
            <div className="text-slate-600 text-xs mt-1">
              Prim oranı, matrah türü ve aktiflik personel kartındaki "Prim / Komisyon Ayarları"ndan okunur.
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
